import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@xtreed/academy-shared/ui';
import { useAssistantStore } from '@/stores/assistant';
import { useAuthStore } from '@/stores/auth';

const SEEN_KEY = 'xtreed-assistant-banner-seen';

function readSeen() {
  try {
    return sessionStorage.getItem(SEEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeSeen(seen: boolean) {
  try {
    if (seen) sessionStorage.setItem(SEEN_KEY, 'true');
    else sessionStorage.removeItem(SEEN_KEY);
  } catch {
    return;
  }
}

useAuthStore.subscribe((state, prev) => {
  if (prev.isAuthenticated && !state.isAuthenticated) writeSeen(false);
});

export function AssistantIntroBanner() {
  const { t } = useTranslation('layout');
  const role = useAuthStore((state) => state.user?.role);
  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const isHide = useAssistantStore((state) => state.isHideAssistantBanner);
  const setHide = useAssistantStore((state) => state.setHideAssistantBanner);
  const setOpen = useAssistantStore((state) => state.setOpen);
  const [open, setOpenDialog] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (!isAdmin || isHide || readSeen()) return;
    writeSeen(true);
    setOpenDialog(true);
  }, [isAdmin, isHide]);

  const handleClose = (openAssistant: boolean) => {
    if (dontShowAgain) setHide(true);
    setOpenDialog(false);
    if (openAssistant) setOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/30">
            <Sparkles className="h-7 w-7 text-primary-ink" />
          </div>
          <DialogTitle>{t('admin.assistant.banner.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.assistant.banner.subtitle')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="items-center sm:justify-between">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
            />
            {t('admin.assistant.banner.dontShowAgain')}
          </label>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => handleClose(false)}>
              {t('admin.assistant.banner.close')}
            </Button>
            <Button onClick={() => handleClose(true)}>
              {t('admin.assistant.banner.seeNow')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
